import { fetchWithAuth } from '../../../common/utils/apiUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

class RunapService {

    getMvtUrlTemplate() {
        return `${API_BASE_URL}/mvt/runap/{z}/{x}/{y}`;
    }

    /**
     * Get all RUNAP areas as GeoJSON
     * @returns {Promise} GeoJSON FeatureCollection
     */
    async getAllRunapsGeoJson() {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/runap/geojson`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return { data };
        } catch (error) {
            console.error('Error fetching RUNAP GeoJSON:', error);
            throw error;
        }
    }

    /**
     * Get all RUNAP areas
     * @returns {Promise} List of RUNAP areas
     */
    async getAllRunaps() {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/runap`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching RUNAP areas:', error);
            throw error;
        }
    }

    /**
     * Get a RUNAP area by ID
     * @param {number} id - RUNAP area ID
     * @returns {Promise} RUNAP area
     */
    async getRunapById(id) {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/runap/${id}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching RUNAP by ID:', error);
            throw error;
        }
    }

    /**
     * Get a RUNAP area by name
     * @param {string} name - RUNAP area name (ap_nombre)
     * @returns {Promise} RUNAP area
     */
    async getRunapByName(name) {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/runap/name/${encodeURIComponent(name)}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching RUNAP by name:', error);
            throw error;
        }
    }

    /**
     * Upload GeoJSON file with RUNAP areas
     * @param {Object} geoJsonData - Complete GeoJSON object
     * @returns {Promise} Response with saved RUNAP areas
     */
    async uploadGeoJson(geoJsonData) {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/runap/upload`, {
                method: 'POST',
                body: JSON.stringify(geoJsonData)
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return { data };
        } catch (error) {
            console.error('Error uploading GeoJSON:', error);
            throw error;
        }
    }

    /**
     * Delete all RUNAP areas
     * @returns {Promise} Deletion confirmation
     */
    async deleteAllRunaps() {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/runap`, {
                method: 'DELETE'
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error deleting RUNAP areas:', error);
            throw error;
        }
    }

    /**
     * Convert RUNAP areas to GeoJSON format for map libraries
     * @param {Array} runaps - Array of RUNAP areas from backend
     * @returns {Object} GeoJSON FeatureCollection
     */
    convertToGeoJSON(runaps) {
        return {
            type: 'FeatureCollection',
            features: runaps.map(runap => ({
                type: 'Feature',
                properties: {
                    id: runap.id,
                    fid: runap.fid,
                    condicion: runap.condicion,
                    ap_nombre: runap.apNombre,
                    ap_categor: runap.apCategor
                },
                geometry: this.parseGeometry(runap.geometry)
            }))
        };
    }

    /**
     * Parse geometry string to GeoJSON
     * @param {string|Object} geometry - Geometry from backend
     * @returns {Object} Geometry in GeoJSON format
     */
    parseGeometry(geometry) {
        if (!geometry) {
            return null;
        }
        
        if (typeof geometry === 'string') {
            try {
                return JSON.parse(geometry);
            } catch (e) {
                console.error('Error parsing geometry:', e);
                return null;
            }
        }
        
        return geometry;
    }
}

export default new RunapService();
